import React, { useEffect, useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  FiFilter,
  FiMoreVertical,
  FiCheck,
  FiX,
  FiClock,
  FiAlertCircle,
  FiExternalLink,
  FiUser,
  FiPhone,
  FiMail,
} from "react-icons/fi";
import {
  getPartnershipData,
  getUserPartnershipDataFilterAPI,
} from "../../API/DashboardAPI/AdminAPI";
import ButtonLoader from "../button/ButtonLoader";
import SimplePagination from "../Pagination/SimplePagination";
import { Edit_PartnershipStatus_API } from "../../API/PartnershipAPI/PartnershipAPI";

export function UserPartnershipTable() {
  const [pageNumber, setPageNumber] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [statusChange, setstatusChange] = useState({
    loader: false,
    _id: null,
    type: 0, // (0 incomple , complete) || (1 rejected)
  });
  const [limit, setLimit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [rejectionModal, setRejectionModal] = useState({
    isOpen: false,
    partnership: null,
    message: "",
  });
  const [selectedPartnership, setSelectedPartnership] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  const menuRef = useRef(null);
  const queryClient = useQueryClient();

  const {
    data: PartData,
    isLoading: isPartLoading,
    error: partError,
    refetch,
  } = useQuery({
    queryKey: ["usersPartnership", pageNumber, statusFilter, limit],
    queryFn: () =>
      statusFilter !== null
        ? getUserPartnershipDataFilterAPI(pageNumber, statusFilter, limit)
        : getPartnershipData(pageNumber, limit),
    staleTime: 0,
    keepPreviousData: false,
  });

  const handleLimitChange = async (newLimit) => {
    setLimit(newLimit);
    setPageNumber(1);
    refetch();
  };

  useEffect(() => {
    if (PartData?.data?.paginationInfo) {
      setTotalItems(PartData.data.paginationInfo.totalItems);
      setTotalPages(PartData?.data?.paginationInfo?.totalPages);
    }
  }, [PartData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedElement = event.target.closest("#BsThreeDotsVertical");
      if (clickedElement) {
        const value = clickedElement.getAttribute("value");
        if (
          statusChange._id &&
          value &&
          statusChange._id.toString() === value.toString()
        ) {
          setstatusChange({ loader: false, _id: null });
        } else {
          setstatusChange(() => ({
            loader: false,
            _id: value,
          }));
        }
      } else if (menuRef.current && !menuRef.current.contains(event.target)) {
        setstatusChange({ loader: false, _id: null });
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (partError) {
    return <div>Error: {partError.message}</div>;
  }

  const handleFilter = (status) => {
    setStatusFilter(status);
    setPageNumber(1);
  };

  const handleEditPartnershipStatus = async (partnershipStatus) => {
    try {
      setstatusChange({
        ...statusChange,
        loader: true,
        type: 0,
      });

      const updatedStatus =
        partnershipStatus.partnerShipStatus.status === 0 ||
        partnershipStatus.partnerShipStatus.status === 2
          ? 1
          : 0;

      console.log(partnershipStatus.user._id);

      await Edit_PartnershipStatus_API({
        partnerShipId: partnershipStatus.partnerShip._id,
        status: updatedStatus,
        userId: partnershipStatus.user._id,
      });

      // Update the local cache data
      const updatedData = PartData?.data?.partnerShips.map((item) => {
        if (
          item.partnerShipStatus._id === partnershipStatus.partnerShipStatus._id
        ) {
          return {
            ...item,
            partnerShipStatus: {
              ...item.partnerShipStatus,
              status: updatedStatus,
            },
          };
        }
        return item;
      });

      // Use the setQueryData method to update the query cache
      queryClient.setQueryData(
        ["usersPartnership", pageNumber, statusFilter, limit],
        (oldData) => ({
          ...oldData,
          data: {
            ...oldData.data,
            partnerShips: updatedData,
          },
        })
      );

      setstatusChange({
        loader: false,
        _id: null,
        type: null,
      });
    } catch (error) {
      console.error("Error updating partnership status:", error);
      setstatusChange({
        loader: false,
        _id: null,
        type: null,
      });
    }
  };

  const handleRejectPartnership = async (
    partnershipStatus,
    rejectionMessage = ""
  ) => {
    try {
      setstatusChange({
        ...statusChange,
        loader: true,
        type: 1,
      });

      await Edit_PartnershipStatus_API({
        partnerShipId: partnershipStatus.partnerShip._id,
        status: 3,
        userId: partnershipStatus.user._id,
        rejectionResponse: rejectionMessage,
      });

      // Update the local cache data
      const updatedData = PartData?.data?.partnerShips.map((item) => {
        if (
          item.partnerShipStatus._id === partnershipStatus.partnerShipStatus._id
        ) {
          return {
            ...item,
            partnerShipStatus: {
              ...item.partnerShipStatus,
              status: 3,
              rejectionResponse: rejectionMessage,
            },
          };
        }
        return item;
      });

      // Use the setQueryData method to update the query cache
      queryClient.setQueryData(
        ["usersPartnership", pageNumber, statusFilter, limit],
        (oldData) => ({
          ...oldData,
          data: {
            ...oldData.data,
            partnerShips: updatedData,
          },
        })
      );
    } catch (error) {
      console.error("Error rejecting partnership:", error);
    } finally {
      setstatusChange({
        loader: false,
        _id: null,
        type: null,
      });
    }
  };

  const handlePageChange = (page) => setPageNumber(page);

  const handlePartnershipClick = (partnership) => {
    setSelectedPartnership(partnership);
    setIsDetailsModalOpen(true);
  };

  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedPartnership(null);
  };

  return (
    <div className="w-full h-full">
      {/* Rejection Modal */}
      {rejectionModal.isOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Reject Partnership
              </h3>
            </div>
            <div className="p-6">
              <textarea
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--primary] focus:border-transparent transition-colors resize-none"
                rows="4"
                placeholder="Enter rejection reason..."
                value={rejectionModal.message}
                onChange={(e) =>
                  setRejectionModal((prev) => ({
                    ...prev,
                    message: e.target.value,
                  }))
                }
              />
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                onClick={() =>
                  setRejectionModal({
                    isOpen: false,
                    partnership: null,
                    message: "",
                  })
                }
              >
                Cancel
              </button>
              {rejectionModal.message && (
                <button
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium flex items-center gap-2"
                  onClick={async () => {
                    await handleRejectPartnership(
                      rejectionModal.partnership,
                      rejectionModal.message
                    );
                    setRejectionModal({
                      isOpen: false,
                      partnership: null,
                      message: "",
                    });
                  }}
                >
                  <FiX className="w-4 h-4" />
                  Confirm Reject
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Partnership Applications
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Manage user partnership applications and their status
            </p>
          </div>
          <div className="relative">
            <button
              onClick={() => setFilterOpen((prev) => !prev)}
              className="bg-[--primary] text-white px-4 py-2 rounded-lg hover:bg-[--primary]/90 transition-colors flex items-center gap-2 font-medium shadow-sm"
            >
              <FiFilter className="w-4 h-4" />
              Filter
            </button>
            {filterOpen && (
              <div className="absolute top-12 right-0 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                <div className="py-2">
                  <button
                    onClick={() => handleFilter(1)}
                    className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <FiCheck className="w-4 h-4 text-green-500" />
                    Complete
                  </button>
                  <button
                    onClick={() => handleFilter(0)}
                    className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <FiAlertCircle className="w-4 h-4 text-orange-500" />
                    Incomplete
                  </button>
                  <button
                    onClick={() => handleFilter(2)}
                    className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <FiClock className="w-4 h-4 text-yellow-500" />
                    Pending Approval
                  </button>
                  <button
                    onClick={() => handleFilter(3)}
                    className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <FiX className="w-4 h-4 text-red-500" />
                    Rejected
                  </button>
                  <div className="border-t border-gray-200 my-1"></div>
                  <button
                    onClick={() => handleFilter(null)}
                    className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-50"
                  >
                    All Applications
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {isPartLoading ? (
        <div className="py-20 w-full flex justify-center items-center">
          <ButtonLoader />
        </div>
      ) : PartData?.data?.partnerShips?.length > 0 ? (
        <div className="divide-y divide-gray-200">
          {PartData.data.partnerShips.map((rowData) => (
            <div
              key={rowData.partnerShipStatus._id}
              className="p-6 hover:bg-gray-50 transition-colors cursor-pointer"
              onClick={() => handlePartnershipClick(rowData)}
            >
              <div className="flex items-start justify-between gap-6">
                {/* Partnership Info */}
                <div className="flex items-start gap-4 flex-1">
                  <div className="flex-shrink-0">
                    <img
                      src={rowData.partnerShip.companyLogo}
                      alt={rowData.partnerShip.title}
                      className="w-12 h-12 rounded-lg object-cover border border-gray-200"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-medium text-gray-900 mb-1">
                      {rowData.partnerShip.title}
                    </h3>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <FiUser className="w-4 h-4" />
                        <span>
                          {rowData.user.firstName} {rowData.user.lastName}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <FiPhone className="w-4 h-4" />
                        <span>{rowData.user.phoneNumber}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Status Badge */}
                <div className="flex-shrink-0">
                  <span
                    className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                      rowData.partnerShipStatus.status === 1
                        ? "bg-green-100 text-green-800"
                        : rowData.partnerShipStatus.status === 0
                        ? "bg-orange-100 text-orange-800"
                        : rowData.partnerShipStatus.status === 2
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {rowData.partnerShipStatus.status === 1 && (
                      <>
                        <FiCheck className="w-3 h-3" />
                        Completed
                      </>
                    )}
                    {rowData.partnerShipStatus.status === 0 && (
                      <>
                        <FiAlertCircle className="w-3 h-3" />
                        Incomplete
                      </>
                    )}
                    {rowData.partnerShipStatus.status === 2 && (
                      <>
                        <FiClock className="w-3 h-3" />
                        Pending Approval
                      </>
                    )}
                    {rowData.partnerShipStatus.status === 3 && (
                      <>
                        <FiX className="w-3 h-3" />
                        Rejected
                      </>
                    )}
                  </span>
                  {rowData.partnerShipStatus.status === 3 &&
                    rowData.partnerShipStatus.rejectionResponse && (
                      <div className="mt-2 text-xs text-gray-500 max-w-xs">
                        <strong>Reason:</strong>{" "}
                        {rowData.partnerShipStatus.rejectionResponse}
                      </div>
                    )}
                </div>

                {/* Social Media Links */}
                {rowData.partnerShipStatus.socialMediaLinks.length > 0 && (
                  <div className="flex-shrink-0">
                    <div className="space-y-1">
                      {rowData.partnerShipStatus.socialMediaLinks.map(
                        (item) => (
                          <div key={item._id} className="space-y-1">
                            {item.link && (
                              <a
                                href={item.link}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center gap-1 text-sm text-[--primary] hover:text-[--primary]/80 transition-colors"
                              >
                                <FiExternalLink className="w-3 h-3" />
                                {item.title}
                              </a>
                            )}
                            {item.videoUrl && (
                              <a
                                href={item.videoUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center gap-1 text-sm text-[--primary] hover:text-[--primary]/80 transition-colors"
                              >
                                <FiExternalLink className="w-3 h-3" />
                                {item.title} Video
                              </a>
                            )}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}

                {/* Actions */}
                {!(
                  rowData.partnerShipStatus.status === 1 ||
                  rowData.partnerShipStatus.status === 3
                ) &&
                  rowData.partnerShipStatus.socialMediaLinks.length > 0 && (
                    <div
                      className="flex-shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div
                        ref={menuRef}
                        className="relative inline-block text-left"
                        id="BsThreeDotsVertical"
                        value={rowData.partnerShipStatus._id}
                      >
                        <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                          <FiMoreVertical className="w-4 h-4" />
                        </button>

                        {statusChange._id === rowData.partnerShipStatus._id && (
                          <div className="absolute top-10 right-0 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-40">
                            <div className="py-2">
                              {!(rowData.partnerShipStatus.status === 1) && (
                                <button
                                  onClick={async () => {
                                    await handleEditPartnershipStatus(rowData);
                                  }}
                                  disabled={statusChange.loader}
                                  className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-50 flex items-center gap-2 relative"
                                >
                                  {statusChange.loader &&
                                    statusChange.type === 0 && (
                                      <div className="absolute inset-0 flex justify-center items-center bg-white bg-opacity-75">
                                        <ButtonLoader />
                                      </div>
                                    )}
                                  <FiCheck className="w-4 h-4" />
                                  {rowData.partnerShipStatus.status === 0 ||
                                  rowData.partnerShipStatus.status === 2
                                    ? "Mark as Complete"
                                    : "Mark as Incomplete"}
                                </button>
                              )}
                              {!(rowData.partnerShipStatus.status === 3) && (
                                <button
                                  onClick={() => {
                                    setRejectionModal({
                                      isOpen: true,
                                      partnership: rowData,
                                      message: "",
                                    });
                                    setstatusChange({
                                      loader: false,
                                      _id: null,
                                      type: null,
                                    });
                                  }}
                                  className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-50 flex items-center gap-2 relative"
                                >
                                  {statusChange.loader &&
                                    statusChange.type === 1 && (
                                      <div className="absolute inset-0 flex justify-center items-center bg-white bg-opacity-75">
                                        <ButtonLoader />
                                      </div>
                                    )}
                                  <FiX className="w-4 h-4" />
                                  Reject
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <FiUser className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Partnership Applications
          </h3>
          <p className="text-gray-500">
            No user partnership applications found.
          </p>
        </div>
      )}
      <SimplePagination
        totalPages={totalPages}
        currentPage={pageNumber}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        limit={limit}
        totalItems={totalItems}
      />

      {/* Partnership Details Modal */}
      {isDetailsModalOpen && selectedPartnership && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  Partnership Application Details
                </h2>
                <button
                  onClick={handleCloseDetailsModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FiX className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Partnership Information */}
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                      <FiUser className="w-5 h-5" />
                      Partnership Information
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                      {/* Company Logo and Title */}
                      <div className="flex items-center gap-4">
                        <img
                          src={selectedPartnership.partnerShip.companyLogo}
                          alt={selectedPartnership.partnerShip.title}
                          className="w-16 h-16 rounded-lg object-cover border border-gray-200"
                        />
                        <div>
                          <h4 className="text-lg font-medium text-gray-900">
                            {selectedPartnership.partnerShip.title}
                          </h4>
                          <p className="text-sm text-gray-600">
                            Partnership Company
                          </p>
                        </div>
                      </div>

                      {/* Partnership Description */}
                      {selectedPartnership.partnerShip.description && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description
                          </label>
                          <p className="text-sm text-gray-900 leading-relaxed">
                            {selectedPartnership.partnerShip.description}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Social Media Links */}
                  {selectedPartnership.partnerShipStatus.socialMediaLinks
                    .length > 0 && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                        <FiExternalLink className="w-5 h-5" />
                        Social Media Links
                      </h3>
                      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                        {selectedPartnership.partnerShipStatus.socialMediaLinks.map(
                          (item) => (
                            <div key={item._id} className="space-y-2">
                              <h4 className="font-medium text-gray-900">
                                {item.title}
                              </h4>
                              {item.link && (
                                <a
                                  href={item.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2 text-sm text-[--primary] hover:text-[--primary]/80 transition-colors"
                                >
                                  <FiExternalLink className="w-4 h-4" />
                                  {item.link}
                                </a>
                              )}
                              {item.videoUrl && (
                                <a
                                  href={item.videoUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2 text-sm text-[--primary] hover:text-[--primary]/80 transition-colors"
                                >
                                  <FiExternalLink className="w-4 h-4" />
                                  Video: {item.videoUrl}
                                </a>
                              )}
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* User Information */}
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                      <FiUser className="w-5 h-5" />
                      User Information
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                      {/* User Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Full Name
                        </label>
                        <p className="text-sm text-gray-900">
                          {selectedPartnership.user.firstName}{" "}
                          {selectedPartnership.user.lastName}
                        </p>
                      </div>

                      {/* Phone Number */}
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                          <FiPhone className="w-4 h-4" />
                          Phone Number
                        </label>
                        <p className="text-sm text-gray-900">
                          {selectedPartnership.user.phoneNumber}
                        </p>
                      </div>

                      {/* Email */}
                      {selectedPartnership.user.email && (
                        <div>
                          <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                            <FiMail className="w-4 h-4" />
                            Email
                          </label>
                          <p className="text-sm text-gray-900">
                            {selectedPartnership.user.email}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Application Status */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                      <FiClock className="w-5 h-5" />
                      Application Status
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Current Status
                        </label>
                        <span
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                            selectedPartnership.partnerShipStatus.status === 1
                              ? "bg-green-100 text-green-800"
                              : selectedPartnership.partnerShipStatus.status ===
                                0
                              ? "bg-orange-100 text-orange-800"
                              : selectedPartnership.partnerShipStatus.status ===
                                2
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {selectedPartnership.partnerShipStatus.status ===
                            1 && (
                            <>
                              <FiCheck className="w-3 h-3" />
                              Completed
                            </>
                          )}
                          {selectedPartnership.partnerShipStatus.status ===
                            0 && (
                            <>
                              <FiAlertCircle className="w-3 h-3" />
                              Incomplete
                            </>
                          )}
                          {selectedPartnership.partnerShipStatus.status ===
                            2 && (
                            <>
                              <FiClock className="w-3 h-3" />
                              Pending Approval
                            </>
                          )}
                          {selectedPartnership.partnerShipStatus.status ===
                            3 && (
                            <>
                              <FiX className="w-3 h-3" />
                              Rejected
                            </>
                          )}
                        </span>
                      </div>

                      {/* Rejection Reason */}
                      {selectedPartnership.partnerShipStatus.status === 3 &&
                        selectedPartnership.partnerShipStatus
                          .rejectionResponse && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Rejection Reason
                            </label>
                            <p className="text-sm text-gray-900 bg-red-50 p-3 rounded-lg border border-red-200">
                              {
                                selectedPartnership.partnerShipStatus
                                  .rejectionResponse
                              }
                            </p>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={handleCloseDetailsModal}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
